import { toError } from '@fruitful/fruitils';
import {
    type AssistantContentPart,
    type ChatMessage,
    CONVERSATIONAL_MM_AGENT,
    DATA_OFF_TOPIC_CHUNK_TYPE,
    DATA_SUPPORT_ESCALATION_CHUNK_TYPE,
    DATA_THREAD_METRICS_CHUNK_TYPE,
    DATA_UI_CHUNK_TYPE,
    getErrorTextFromMastraSSEData,
    inferThreadMetadataFromMessages,
    type MastraChatConfig,
    type MastraChatReturn,
    type MastraSSEData,
    type MastraSupportEscalationStreamData,
    mergeThreadMetricsPatch,
    parseThreadMetricsPatchFromSSE,
    parseUIChunkFromSSE,
    reorderAssistantContentParts,
    type SendSilentMessageOptions,
    shouldShowUiWhileStreaming,
    type ThreadMetricsSurfaceState,
} from '@fruitful/llm-service-shared';
import {
    AnalyticsUserEvent,
    type CMMBranch,
    type OnboardingChatModule,
} from '@fruitful/types';
import { MastraClientError } from '@mastra/client-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { v4 as uuidv4 } from 'uuid';

import {
    ensureCurrentSessionJwt,
    fetchWithCurrentSessionJwt,
} from '../../auth/fetchWithCurrentSessionJwt';
import { getAuthorizationHeader } from '../../auth/stytchHelpers';
import storage from '../../common/providers/StorageFacade';
import { LLM_SERVICE_BASE_URL } from '../../config/config';
import { getWebUrl } from '../../lib/Web';
import { captureDebug, captureException, track } from '../../logger/Logger';
import { useUpsertVisitor } from '../../user/hooks/useUpsertVisitor';
import { readSproutStreamSelectionForThread } from '../utils/sproutStreamOptionsStorage';
import { useMastraThreadMessages } from './useMastraThreadMessages';

const STREAM_ENDED_UNEXPECTEDLY_ERROR_MESSAGE = 'Stream ended unexpectedly';
const HTTP_STATUS_ZERO_ERROR_MESSAGE = 'HTTP error! status: 0';

// Silent client-side retry budget for server-flagged `retryable` errors — a
// transient state-read blip (e.g. while ECS tasks cycle during a deploy) that
// happens before anything is persisted or streamed. One entry == one retry;
// the value is the backoff before it. Kept short so the member just sees a
// brief pause instead of an error they have to dismiss and re-send.
const CLIENT_STREAM_RETRY_DELAYS_MS = [400, 1000];

const getMastraStreamErrorMessage = (errorMessage: string): string => {
    const statusMatch = /status:\s*(\d+)/.exec(errorMessage);
    const status = statusMatch?.[1];

    if (status === '401') {
        return 'Your session could not be verified. Please sign out and sign back in.';
    }

    if (status === '403') {
        return 'Sorry, something went wrong. Please try again.';
    }

    if (errorMessage === STREAM_ENDED_UNEXPECTEDLY_ERROR_MESSAGE) {
        return 'The connection was interrupted. Please send your message again.';
    }

    return 'Sorry, something went wrong. Please try again.';
};

const appendTextDelta = (
    parts: AssistantContentPart[],
    delta: string,
): void => {
    const lastPart = parts[parts.length - 1];
    if (lastPart?.type === 'text') {
        lastPart.text += delta;
        return;
    }

    parts.push({ type: 'text', text: delta });
};

const buildAssistantContentFromParts = (
    parts: AssistantContentPart[],
    includeUi: boolean,
): AssistantContentPart[] => {
    const ordered = reorderAssistantContentParts(parts);
    const showUi = includeUi || shouldShowUiWhileStreaming(parts);

    return ordered.filter((part) => {
        if (part.type === 'text') {
            return part.text.length > 0;
        }

        return showUi;
    });
};

const generateMessageId = (): string => {
    return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

const generateDefaultThreadId = (): string => {
    return uuidv4();
};

const SSE_DONE_MARKER = '[DONE]';

const reportMastraChatError = (err: unknown): void => {
    captureException(err);
};

const isThreadNotFoundError = (err: unknown): boolean =>
    err instanceof MastraClientError
        ? err.status === 404
        : err instanceof Error && /\bstatus:\s*404\b/i.test(err.message);

const resolveMemberStreamOverrides = ({
    threadId,
    sproutStreamModelId,
    sproutStreamReasoningEffort,
}: {
    threadId: string;
    sproutStreamModelId?: string;
    sproutStreamReasoningEffort?: string;
}): { model?: string; reasoningEffort?: string } => {
    const storedSelection = readSproutStreamSelectionForThread(
        storage,
        threadId,
    );
    const model = storedSelection?.modelId ?? sproutStreamModelId;
    const reasoningEffort =
        storedSelection?.reasoningEffort ?? sproutStreamReasoningEffort;

    return {
        ...(model != null ? { model } : {}),
        ...(reasoningEffort != null ? { reasoningEffort } : {}),
    };
};

/**
 * Replace an existing message by id, or append when the id is not in the list.
 * Stream updates must not assume the assistant message is always last — a
 * concurrent history restore can reorder the array underneath us.
 */
const replaceMessageById = (
    prev: ChatMessage[],
    messageId: string,
    nextMessage: ChatMessage,
): ChatMessage[] => {
    const index = prev.findIndex((message) => message.id === messageId);
    if (index === -1) {
        return [...prev, nextMessage];
    }

    return [...prev.slice(0, index), nextMessage, ...prev.slice(index + 1)];
};

const createAbortError = (): Error => {
    const error = new Error('Request aborted');
    error.name = 'AbortError';
    return error;
};

const isBackgroundStreamInterruption = (err: unknown): boolean =>
    Platform.OS === 'ios' &&
    err instanceof Error &&
    (err.message === STREAM_ENDED_UNEXPECTEDLY_ERROR_MESSAGE ||
        err.message === HTTP_STATUS_ZERO_ERROR_MESSAGE) &&
    AppState.currentState !== 'active';

const processSSEStreamWithXHR = (
    url: string,
    options: {
        method: string;
        headers: Record<string, string>;
        body: string;
        onChunk: (chunk: string) => void;
        onError: (error: Error) => void;
        onComplete: (receivedDone: boolean) => void;
        signal?: AbortSignal;
    },
): void => {
    const xhr = new XMLHttpRequest();
    let buffer = '';
    let receivedDone = false;
    let isAborted = false;

    xhr.open(options.method, url, true);

    Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
    });

    if (options.signal != null) {
        options.signal.addEventListener('abort', () => {
            isAborted = true;
            xhr.abort();
        });
    }

    let processedLength = 0;

    xhr.onreadystatechange = () => {
        if (
            xhr.readyState === XMLHttpRequest.LOADING ||
            xhr.readyState === XMLHttpRequest.DONE
        ) {
            const currentLength = xhr.responseText.length;
            if (currentLength > processedLength) {
                const newData = xhr.responseText.slice(processedLength);
                buffer += newData;
                processedLength = currentLength;

                const lines = buffer.split('\n');
                buffer = lines.pop() ?? '';

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine.startsWith('data: ')) {
                        continue;
                    }

                    const jsonStr = trimmedLine.slice(6);
                    if (jsonStr === SSE_DONE_MARKER) {
                        receivedDone = true;
                        continue;
                    }
                    if (jsonStr === '') {
                        continue;
                    }

                    options.onChunk(jsonStr);
                }
            }

            if (xhr.readyState === XMLHttpRequest.DONE) {
                if (isAborted) {
                    options.onError(createAbortError());
                    return;
                }

                if (buffer.trim() !== '') {
                    const trimmedLine = buffer.trim();
                    if (trimmedLine.startsWith('data: ')) {
                        const jsonStr = trimmedLine.slice(6);
                        if (jsonStr === SSE_DONE_MARKER) {
                            receivedDone = true;
                        } else if (jsonStr !== '') {
                            options.onChunk(jsonStr);
                        }
                    }
                }

                if (xhr.status >= 200 && xhr.status < 300) {
                    options.onComplete(receivedDone);
                } else {
                    options.onError(
                        new Error(
                            `HTTP error! status: ${xhr.status.toString()}`,
                        ),
                    );
                }
            }
        }
    };

    xhr.onerror = () => {
        options.onError(new Error('Network error'));
    };

    xhr.send(options.body);
};

const OFF_TOPIC_CUTOFF = 4;
const OFF_TOPIC_CUTOFF_MESSAGE =
    "I'm not able to continue this conversation. If you'd like to start over and explore what Fruitful can do for your finances, feel free to come back anytime.";

const hasStoredThread = (storageKey: string | undefined): boolean =>
    storageKey != null && storage.getString(storageKey) != null;

const getActivityStorageKey = (
    threadStorageKey: string | undefined,
): string | undefined =>
    threadStorageKey != null ? `${threadStorageKey}:lastActivityAt` : undefined;

/**
 * Whether a persisted session should be treated as expired on this mount: a
 * stored thread exists, we've recorded a last-activity time, and more than
 * `timeoutMs` has elapsed since. A missing timestamp (sessions predating
 * activity tracking) is treated as NOT stale so we never drop them by surprise.
 */
const isSessionStale = (
    threadStorageKey: string,
    activityStorageKey: string | undefined,
    timeoutMs: number,
): boolean => {
    if (activityStorageKey == null) return false;
    if (storage.getString(threadStorageKey) == null) return false;
    const raw = storage.getString(activityStorageKey);
    if (raw == null) return false;
    const lastActivityAt = Number(raw);
    if (!Number.isFinite(lastActivityAt)) return false;
    return Date.now() - lastActivityAt > timeoutMs;
};

export const useMastraChat = ({
    agent,
    threadId: initialThreadId,
    resourceId,
    initialMessages = [],
    persistThreadStorageKey,
    restoreThreadMessages = false,
    autoGreet = false,
    streamUrl: streamUrlOverride,
    sessionInactivityTimeoutMs,
    onSessionExpired,
    sproutStreamModelId,
    sproutStreamReasoningEffort,
    membershipId,
    onMessageSent,
    onResponseReceived,
}: MastraChatConfig & { membershipId?: string }): MastraChatReturn => {
    const activityStorageKey = getActivityStorageKey(persistThreadStorageKey);

    // Inactivity-based session expiry. On a fresh mount (hard refresh, or
    // navigating away and back), if the persisted session has been idle longer
    // than the configured timeout, drop the stored thread so we start clean —
    // the same net effect as the "Start over" button. Runs at most once per
    // mount, before we read/restore the stored thread below.
    const staleSessionCheckedRef = useRef(false);
    const expiredStaleSessionRef = useRef(false);
    if (!staleSessionCheckedRef.current) {
        staleSessionCheckedRef.current = true;
        if (
            persistThreadStorageKey != null &&
            sessionInactivityTimeoutMs != null &&
            isSessionStale(
                persistThreadStorageKey,
                activityStorageKey,
                sessionInactivityTimeoutMs,
            )
        ) {
            storage.removeItem(persistThreadStorageKey);
            if (activityStorageKey != null) {
                storage.removeItem(activityStorageKey);
            }
            expiredStaleSessionRef.current = true;
        }
    }

    // Notify the caller (after render) when a stale session was dropped, so it
    // can reset any chat-adjacent UI state that outlives the thread.
    const onSessionExpiredRef = useRef(onSessionExpired);
    onSessionExpiredRef.current = onSessionExpired;
    useEffect(() => {
        if (expiredStaleSessionRef.current) {
            onSessionExpiredRef.current?.();
        }
    }, []);

    const hadStoredThreadOnMount = hasStoredThread(persistThreadStorageKey);

    const getInitialThreadId = (): string => {
        if (initialThreadId != null) {
            return initialThreadId;
        }

        if (persistThreadStorageKey != null) {
            const storedThreadId = storage.getString(persistThreadStorageKey);
            if (storedThreadId != null) {
                return storedThreadId;
            }
        }

        return generateDefaultThreadId();
    };

    const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
    const [threadMetadata, setThreadMetadata] =
        useState<ThreadMetricsSurfaceState | null>(null);
    const pendingCollectedModulesRef = useRef<string[] | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    // isStreaming is async React state — it doesn't reflect a send until
    // a render happens, so two calls to sendMessage/sendMessageWithAgentPayload
    // in the same tick (a double-tap, or two widgets firing off one user
    // action) can both see isStreaming as false and both go through,
    // producing two independent backend turns for one message. This ref
    // closes that window synchronously; the effect below keeps it in sync
    // with isStreaming once the real state catches up.
    const sendGuardRef = useRef(false);
    useEffect(() => {
        sendGuardRef.current = isStreaming;
    }, [isStreaming]);
    const [showTypingIndicator, setShowTypingIndicator] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [isRestoringHistory, setIsRestoringHistory] = useState(
        () => restoreThreadMessages && hadStoredThreadOnMount,
    );
    const [threadId, setThreadId] = useState(getInitialThreadId);
    const fetchThreadMessages = useMastraThreadMessages();
    const fetchThreadMessagesRef = useRef(fetchThreadMessages);
    fetchThreadMessagesRef.current = fetchThreadMessages;

    const currentThreadId = useRef(threadId);
    const sproutStreamModelIdRef = useRef(sproutStreamModelId);
    sproutStreamModelIdRef.current = sproutStreamModelId;
    const sproutStreamReasoningEffortRef = useRef(sproutStreamReasoningEffort);
    sproutStreamReasoningEffortRef.current = sproutStreamReasoningEffort;
    const membershipIdRef = useRef(membershipId);
    membershipIdRef.current = membershipId;
    const abortControllerRef = useRef<AbortController | null>(null);
    const offTopicCountRef = useRef(0);
    const restoreGenerationRef = useRef(0);
    const streamGenerationRef = useRef(0);
    const hasGreetedRef = useRef(false);
    const hasExistingConversationRef = useRef(hadStoredThreadOnMount);
    /** Thread id whose history has already been hydrated (or intentionally skipped). */
    const restoredThreadIdRef = useRef<string | null>(null);
    /**
     * Local messages for the in-flight turn (user bubble + streaming assistant).
     * Kept across a history restore so server history that hasn't persisted the
     * turn yet cannot wipe them from the UI.
     */
    const inFlightTurnRef = useRef<ChatMessage[]>([]);
    const hasMessagesRef = useRef(initialMessages.length > 0);
    hasMessagesRef.current = messages.length > 0;
    const upsertVisitor = useUpsertVisitor();

    const setActiveThreadId = useCallback((nextThreadId: string) => {
        currentThreadId.current = nextThreadId;
        setThreadId(nextThreadId);
    }, []);

    const markSessionActivity = useCallback(() => {
        if (activityStorageKey == null || sessionInactivityTimeoutMs == null) {
            return;
        }

        storage.setString(activityStorageKey, String(Date.now()));
    }, [activityStorageKey, sessionInactivityTimeoutMs]);

    const persistThreadId = useCallback(() => {
        if (persistThreadStorageKey == null) {
            return;
        }

        storage.setString(persistThreadStorageKey, currentThreadId.current);
        markSessionActivity();
    }, [persistThreadStorageKey, markSessionActivity]);

    const applyRestoredMessages = useCallback(
        (restoredMessages: ChatMessage[]) => {
            setMessages(() => {
                const sorted = [...restoredMessages].sort((a, b) => {
                    const timeDiff =
                        a.createdAt.getTime() - b.createdAt.getTime();
                    if (timeDiff !== 0) return timeDiff;
                    if (a.role === 'user' && b.role === 'assistant') return -1;
                    if (a.role === 'assistant' && b.role === 'user') return 1;
                    return 0;
                });
                const inFlight = inFlightTurnRef.current;
                if (inFlight.length === 0) {
                    return sorted;
                }

                return [...sorted, ...inFlight];
            });
        },
        [],
    );

    const beginInFlightTurn = useCallback((userMessage: ChatMessage) => {
        inFlightTurnRef.current = [userMessage];
    }, []);

    const trackInFlightAssistant = useCallback(
        (assistantMessage: ChatMessage) => {
            const withoutAssistant = inFlightTurnRef.current.filter(
                (message) => message.role !== 'assistant',
            );
            inFlightTurnRef.current = [...withoutAssistant, assistantMessage];
        },
        [],
    );

    const clearInFlightTurn = useCallback(() => {
        inFlightTurnRef.current = [];
    }, []);

    useEffect(() => {
        if (!restoreThreadMessages || persistThreadStorageKey == null) {
            return undefined;
        }

        const storedThreadId = storage.getString(persistThreadStorageKey);
        if (storedThreadId == null) {
            return undefined;
        }

        // hydrate each thread at most once — do not re-run when the Mastra
        // client / fetcher identity churns on Stytch session refresh.
        if (restoredThreadIdRef.current === storedThreadId) {
            return undefined;
        }

        restoredThreadIdRef.current = storedThreadId;
        setActiveThreadId(storedThreadId);
        const generation = ++restoreGenerationRef.current;
        // only block the composer on the initial empty-state hydrate.
        const shouldShowRestoringSpinner = !hasMessagesRef.current;
        if (shouldShowRestoringSpinner) {
            setIsRestoringHistory(true);
        }

        const restoreHistory = async () => {
            try {
                const { chatMessages: restoredMessages, rawMessages } =
                    await fetchThreadMessagesRef.current({
                        agentId: agent.agentKey,
                        threadId: storedThreadId,
                    });

                if (restoreGenerationRef.current !== generation) {
                    return;
                }

                if (restoredMessages.length > 0) {
                    applyRestoredMessages(restoredMessages);

                    if (agent.agentKey === CONVERSATIONAL_MM_AGENT.agentKey) {
                        const inferred =
                            inferThreadMetadataFromMessages(rawMessages);
                        if (inferred != null) {
                            setThreadMetadata(inferred);
                        }
                    }
                }
            } catch (err) {
                if (restoreGenerationRef.current !== generation) {
                    return;
                }

                // only forget the pointer when the server says the thread
                // is gone. Transient failures keep the id so a remount can
                // recover history.
                if (isThreadNotFoundError(err)) {
                    storage.removeItem(persistThreadStorageKey);
                    const newThreadId = generateDefaultThreadId();
                    setActiveThreadId(newThreadId);
                    restoredThreadIdRef.current = newThreadId;
                    hasExistingConversationRef.current = false;
                } else {
                    restoredThreadIdRef.current = null;
                    setError(toError(err));
                }
                reportMastraChatError(err);
            } finally {
                if (
                    restoreGenerationRef.current === generation &&
                    shouldShowRestoringSpinner
                ) {
                    setIsRestoringHistory(false);
                }
            }
        };

        void restoreHistory();

        return () => {
            restoreGenerationRef.current += 1;
        };
    }, [
        agent.agentKey,
        applyRestoredMessages,
        persistThreadStorageKey,
        restoreThreadMessages,
        setActiveThreadId,
    ]);

    const stopStreaming = useCallback(() => {
        if (abortControllerRef.current != null) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsStreaming(false);
        setShowTypingIndicator(false);
        clearInFlightTurn();
    }, [clearInFlightTurn]);

    const clearMessages = useCallback(() => {
        stopStreaming();
        setMessages([]);
        setThreadMetadata(null);
        setShowTypingIndicator(false);
        setError(null);
        offTopicCountRef.current = 0;
        hasGreetedRef.current = false;
        hasExistingConversationRef.current = false;
        restoredThreadIdRef.current = null;
        clearInFlightTurn();
        if (persistThreadStorageKey != null) {
            storage.removeItem(persistThreadStorageKey);
        }
        setActiveThreadId(generateDefaultThreadId());
    }, [
        clearInFlightTurn,
        persistThreadStorageKey,
        setActiveThreadId,
        stopStreaming,
    ]);

    const switchToThread = useCallback(
        async (nextThreadId: string) => {
            if (nextThreadId === currentThreadId.current) {
                return;
            }

            stopStreaming();
            setError(null);
            setThreadMetadata(null);
            setShowTypingIndicator(false);
            setMessages([]);
            clearInFlightTurn();
            offTopicCountRef.current = 0;
            hasGreetedRef.current = true;
            hasExistingConversationRef.current = true;
            setActiveThreadId(nextThreadId);

            if (persistThreadStorageKey != null) {
                storage.setString(persistThreadStorageKey, nextThreadId);
            }

            const generation = ++restoreGenerationRef.current;
            setIsRestoringHistory(true);

            try {
                const { chatMessages: restoredMessages } =
                    await fetchThreadMessagesRef.current({
                        agentId: agent.agentKey,
                        threadId: nextThreadId,
                    });

                if (restoreGenerationRef.current !== generation) {
                    return;
                }

                restoredThreadIdRef.current = nextThreadId;
                setMessages(
                    [...restoredMessages].sort((a, b) => {
                        const timeDiff =
                            a.createdAt.getTime() - b.createdAt.getTime();
                        if (timeDiff !== 0) return timeDiff;
                        if (a.role === 'user' && b.role === 'assistant')
                            return -1;
                        if (a.role === 'assistant' && b.role === 'user')
                            return 1;
                        return 0;
                    }),
                );
            } catch (err) {
                if (restoreGenerationRef.current !== generation) {
                    return;
                }

                reportMastraChatError(err);
                setError(toError(err));
            } finally {
                if (restoreGenerationRef.current === generation) {
                    setIsRestoringHistory(false);
                }
            }
        },
        [
            agent.agentKey,
            clearInFlightTurn,
            persistThreadStorageKey,
            setActiveThreadId,
            stopStreaming,
        ],
    );

    /**
     * Stream a response from the agent. Handles both iOS (XHR) and web (fetch) paths.
     * Only manages the assistant message — caller is responsible for adding user messages.
     */
    // Ref to the latest `streamResponse` so it can re-invoke itself for a
    // silent retry without needing itself as a dependency. Assigned just after
    // the useCallback below.
    const streamResponseRef = useRef<
        ((userContent: string, attempt: number) => Promise<void>) | null
    >(null);

    const streamResponse = useCallback(
        async (userContent: string, attempt = 0) => {
            setError(null);
            setIsStreaming(true);
            persistThreadId();
            upsertVisitor();
            const generation = ++streamGenerationRef.current;
            abortControllerRef.current = new AbortController();
            const attemptAbortController = abortControllerRef.current;
            const state = {
                assistantContentParts: [] as AssistantContentPart[],
                assistantMessageId: null as string | null,
                // Set by handleChunk when the server flags a transient,
                // retryable error before any output — handled after the stream
                // closes by maybeRetryOrFinalize (silent re-send).
                retryableErrorPending: false,
            };

            const finalizeMessage = (
                assistantMessageId: string | null,
                finalState?: {
                    assistantContentParts: AssistantContentPart[];
                },
            ) => {
                if (pendingCollectedModulesRef.current != null) {
                    const modules = pendingCollectedModulesRef.current;
                    pendingCollectedModulesRef.current = null;
                    setThreadMetadata((prev) =>
                        mergeThreadMetricsPatch(prev, {
                            collectedModules: modules,
                        }),
                    );
                }

                if (assistantMessageId != null) {
                    setMessages((prev) => {
                        const existing = prev.find(
                            (message) => message.id === assistantMessageId,
                        );
                        if (existing?.isStreaming !== true) {
                            return prev;
                        }

                        const finalMessage = {
                            ...existing,
                            isStreaming: false,
                            content: buildAssistantContentFromParts(
                                finalState?.assistantContentParts ?? [],
                                true,
                            ),
                        };
                        onResponseReceived?.(finalMessage);
                        return replaceMessageById(
                            prev,
                            assistantMessageId,
                            finalMessage,
                        );
                    });
                    // Agent finished responding — refresh the idle clock so
                    // the inactivity window is measured from the last thing the
                    // member saw, not just from when they last sent.
                    markSessionActivity();
                }
            };

            // After the stream closes: if the server flagged a transient,
            // retryable error before any output, silently re-send the same turn
            // (nothing was persisted/streamed) instead of finalizing an error.
            // Bail if the turn was superseded or aborted while we waited.
            const maybeRetryOrFinalize = async (): Promise<void> => {
                const canRetry =
                    state.retryableErrorPending &&
                    attempt < CLIENT_STREAM_RETRY_DELAYS_MS.length &&
                    streamGenerationRef.current === generation &&
                    !attemptAbortController.signal.aborted;

                if (!canRetry) {
                    finalizeMessage(state.assistantMessageId, state);
                    return;
                }

                const backoffMs = CLIENT_STREAM_RETRY_DELAYS_MS[attempt] ?? 0;
                await new Promise<void>((resolve) => {
                    setTimeout(resolve, backoffMs);
                });

                // Re-check after the wait — a newer turn, thread switch, or
                // stop could have superseded this one.
                if (
                    streamGenerationRef.current !== generation ||
                    attemptAbortController.signal.aborted ||
                    abortControllerRef.current !== attemptAbortController
                ) {
                    return;
                }

                await streamResponseRef.current?.(userContent, attempt + 1);
            };

            try {
                const url =
                    streamUrlOverride ??
                    `${LLM_SERVICE_BASE_URL}/member/agents/${agent.agentKey}/stream`;

                captureDebug(
                    `[useMastraChat] DEPLOY CANARY 2026-08-25 — sending to ${url}, threadId=${currentThreadId.current?.slice(0, 8)}, agent=${agent.id}`,
                );

                await ensureCurrentSessionJwt();
                const authHeader = getAuthorizationHeader();
                const requestConfig = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...authHeader,
                        ...(Platform.OS !== 'web'
                            ? { Origin: getWebUrl() }
                            : {}),
                    },
                    body: JSON.stringify({
                        messages: [{ role: 'user', content: userContent }],
                        memory: {
                            thread: {
                                id: currentThreadId.current,
                                metadata: { agentId: agent.id },
                            },
                            resource: resourceId,
                        },
                        // IANA zone for server-side DST-correct conversion
                        // (Node Intl). minutesOffset remains for FTS MCP and
                        // as a Hermes-safe fallback when timeZone is absent.
                        timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        minutesOffset: new Date().getTimezoneOffset(),
                        ...(membershipIdRef.current != null &&
                        membershipIdRef.current !== ''
                            ? { membershipId: membershipIdRef.current }
                            : {}),
                        ...resolveMemberStreamOverrides({
                            threadId: currentThreadId.current,
                            sproutStreamModelId: sproutStreamModelIdRef.current,
                            sproutStreamReasoningEffort:
                                sproutStreamReasoningEffortRef.current,
                        }),
                    }),
                    signal: abortControllerRef.current?.signal,
                };

                const upsertStreamingAssistant = (
                    contentParts: AssistantContentPart[],
                ): void => {
                    const isNew = state.assistantMessageId == null;
                    const messageId =
                        state.assistantMessageId ?? generateMessageId();
                    if (isNew) {
                        state.assistantMessageId = messageId;
                    }

                    const newMessage: ChatMessage = {
                        id: messageId,
                        role: 'assistant',
                        content: buildAssistantContentFromParts(
                            contentParts,
                            false,
                        ),
                        createdAt: new Date(),
                        isStreaming: true,
                    };

                    trackInFlightAssistant(newMessage);
                    setMessages((prev) =>
                        isNew
                            ? [...prev, newMessage]
                            : replaceMessageById(prev, messageId, newMessage),
                    );
                };

                const handleChunk = (
                    jsonStr: string,
                    state: {
                        assistantContentParts: AssistantContentPart[];
                        assistantMessageId: string | null;
                        retryableErrorPending: boolean;
                    },
                ) => {
                    try {
                        const data = JSON.parse(jsonStr) as MastraSSEData;

                        if (
                            data.type === 'text-delta' &&
                            data.payload?.text != null
                        ) {
                            if (pendingCollectedModulesRef.current != null) {
                                const modules =
                                    pendingCollectedModulesRef.current;
                                pendingCollectedModulesRef.current = null;
                                setThreadMetadata((prev) =>
                                    mergeThreadMetricsPatch(prev, {
                                        collectedModules: modules,
                                    }),
                                );
                            }

                            appendTextDelta(
                                state.assistantContentParts,
                                data.payload.text,
                            );

                            upsertStreamingAssistant(
                                state.assistantContentParts,
                            );
                        }

                        const uiChunk = parseUIChunkFromSSE(data);
                        if (uiChunk != null) {
                            state.assistantContentParts.push({
                                type: 'ui',
                                widget: uiChunk.widget,
                                props: uiChunk.props,
                            });

                            upsertStreamingAssistant(
                                state.assistantContentParts,
                            );
                        } else if (data.type === DATA_UI_CHUNK_TYPE) {
                            captureException(
                                new Error(
                                    'Unable to parse widget from data-ui stream chunk',
                                ),
                                {
                                    agentKey: agent.agentKey,
                                    threadId: currentThreadId.current,
                                    chunkType: data.type,
                                },
                            );
                        }

                        if (data.type === DATA_THREAD_METRICS_CHUNK_TYPE) {
                            const metricsPatch =
                                parseThreadMetricsPatchFromSSE(data);
                            if (metricsPatch != null) {
                                if (metricsPatch.collectedModules != null) {
                                    pendingCollectedModulesRef.current =
                                        metricsPatch.collectedModules;
                                    const {
                                        collectedModules: _deferred,
                                        ...immediatePatch
                                    } = metricsPatch;
                                    if (
                                        immediatePatch.branch != null ||
                                        immediatePatch.currentStep != null
                                    ) {
                                        setThreadMetadata((prev) =>
                                            mergeThreadMetricsPatch(
                                                prev,
                                                immediatePatch,
                                            ),
                                        );
                                    }
                                } else {
                                    setThreadMetadata((prev) =>
                                        mergeThreadMetricsPatch(
                                            prev,
                                            metricsPatch,
                                        ),
                                    );
                                }
                            }
                        }

                        if (data.type === DATA_OFF_TOPIC_CHUNK_TYPE) {
                            offTopicCountRef.current += 1;
                            track(
                                AnalyticsUserEvent.AI_CHAT_OFF_TOPIC_DETECTED,
                                {
                                    agentId: agent.id,
                                    threadId: currentThreadId.current,
                                    resourceId: resourceId ?? '',
                                    offTopicCount: offTopicCountRef.current,
                                },
                            );
                        }

                        if (data.type === DATA_SUPPORT_ESCALATION_CHUNK_TYPE) {
                            const supportEscalationData = data.data as
                                | MastraSupportEscalationStreamData
                                | undefined;
                            track(
                                AnalyticsUserEvent.AI_CHAT_SUPPORT_ESCALATION_DETECTED,
                                {
                                    agentId: agent.id,
                                    threadId: currentThreadId.current,
                                    resourceId: resourceId ?? '',
                                    trigger:
                                        supportEscalationData?.trigger ?? '',
                                },
                            );
                        }

                        if (
                            data.type === 'error' ||
                            data.error != null ||
                            data.payload?.error != null
                        ) {
                            // Transient, server-flagged state-read blip before
                            // any output (e.g. mid-deploy): defer to a silent
                            // re-send instead of showing the member an error.
                            if (
                                data.payload?.retryable === true &&
                                state.assistantContentParts.length === 0 &&
                                attempt < CLIENT_STREAM_RETRY_DELAYS_MS.length
                            ) {
                                state.retryableErrorPending = true;
                                return;
                            }

                            const errorText =
                                getErrorTextFromMastraSSEData(data);

                            const errorMessage: ChatMessage = {
                                id: generateMessageId(),
                                role: 'assistant',
                                content: [{ type: 'text', text: errorText }],
                                createdAt: new Date(),
                                isError: true,
                            };

                            setMessages((prev) => {
                                if (state.assistantMessageId != null) {
                                    return replaceMessageById(
                                        prev,
                                        state.assistantMessageId,
                                        errorMessage,
                                    );
                                }
                                return [...prev, errorMessage];
                            });
                            trackInFlightAssistant(errorMessage);

                            const err = new Error(errorText);
                            setError(err);
                            reportMastraChatError(err);
                        }
                    } catch {
                        // Ignore JSON parse errors for incomplete chunks
                    }
                };

                const isIOS = Platform.OS === 'ios';

                if (isIOS) {
                    await new Promise<void>((resolve, reject) => {
                        processSSEStreamWithXHR(url, {
                            ...requestConfig,
                            onChunk: (jsonStr) => {
                                handleChunk(jsonStr, state);
                            },
                            onError: (err) => {
                                reject(err);
                            },
                            onComplete: (receivedDone) => {
                                if (!receivedDone) {
                                    reject(
                                        new Error(
                                            STREAM_ENDED_UNEXPECTEDLY_ERROR_MESSAGE,
                                        ),
                                    );
                                    return;
                                }
                                resolve();
                            },
                        });
                    });
                    await maybeRetryOrFinalize();
                    return;
                }

                // Web/Android: fetch with ReadableStream
                const response = await fetchWithCurrentSessionJwt(
                    url,
                    requestConfig,
                );

                if (!response.ok) {
                    throw new Error(
                        `HTTP error! status: ${response.status.toString()}`,
                    );
                }

                if (response.body == null) {
                    throw new Error('No response body received');
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                let buffer = '';
                let receivedDone = false;

                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) {
                            break;
                        }

                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;

                        const lines = buffer.split('\n');
                        buffer = lines.pop() ?? '';

                        for (const line of lines) {
                            const trimmedLine = line.trim();
                            if (!trimmedLine.startsWith('data: ')) {
                                continue;
                            }

                            const jsonStr = trimmedLine.slice(6);
                            if (jsonStr === SSE_DONE_MARKER) {
                                receivedDone = true;
                                continue;
                            }
                            if (jsonStr === '') {
                                continue;
                            }

                            handleChunk(jsonStr, state);
                        }
                    }
                } finally {
                    reader.releaseLock();
                }

                if (!receivedDone) {
                    throw new Error(STREAM_ENDED_UNEXPECTEDLY_ERROR_MESSAGE);
                }

                await maybeRetryOrFinalize();
            } catch (err) {
                finalizeMessage(state.assistantMessageId, state);

                if (err instanceof Error && err.name === 'AbortError') {
                    return;
                }

                const errorObj = toError(err);
                setError(errorObj);
                if (!isBackgroundStreamInterruption(errorObj)) {
                    reportMastraChatError(errorObj);
                }

                const errorMessage: ChatMessage = {
                    id: generateMessageId(),
                    role: 'assistant',
                    content: [
                        {
                            type: 'text',
                            text: getMastraStreamErrorMessage(errorObj.message),
                        },
                    ],
                    createdAt: new Date(),
                    isError: true,
                };
                setMessages((prev) => [...prev, errorMessage]);
            } finally {
                if (streamGenerationRef.current === generation) {
                    setIsStreaming(false);
                    setShowTypingIndicator(false);
                    abortControllerRef.current = null;
                    clearInFlightTurn();
                }
            }
        },
        [
            agent,
            clearInFlightTurn,
            persistThreadId,
            upsertVisitor,
            resourceId,
            onResponseReceived,
            markSessionActivity,
            trackInFlightAssistant,
        ],
    );
    streamResponseRef.current = streamResponse;

    const sendMessage = useCallback(
        async (content: string) => {
            if (content.trim() === '' || sendGuardRef.current) {
                return;
            }
            sendGuardRef.current = true;

            const userMessage: ChatMessage = {
                id: generateMessageId(),
                role: 'user',
                content: [{ type: 'text', text: content }],
                createdAt: new Date(),
            };

            beginInFlightTurn(userMessage);
            setMessages((prev) => [...prev, userMessage]);
            onMessageSent?.(userMessage);
            setShowTypingIndicator(true);

            if (offTopicCountRef.current >= OFF_TOPIC_CUTOFF) {
                const cutoffMessage: ChatMessage = {
                    id: generateMessageId(),
                    role: 'assistant',
                    content: [{ type: 'text', text: OFF_TOPIC_CUTOFF_MESSAGE }],
                    createdAt: new Date(),
                };
                track(AnalyticsUserEvent.AI_CHAT_OFF_TOPIC_CUTOFF_REACHED, {
                    agentId: agent.id,
                    threadId: currentThreadId.current,
                    resourceId: resourceId ?? '',
                });
                setMessages((prev) => [...prev, cutoffMessage]);
                clearInFlightTurn();
                // streamResponse (and its own isStreaming/effect reset)
                // never runs on this path — release the guard directly.
                sendGuardRef.current = false;
                return;
            }

            await streamResponse(content);
        },
        [
            agent.id,
            beginInFlightTurn,
            clearInFlightTurn,
            onMessageSent,
            resourceId,
            streamResponse,
        ],
    );

    const sendMessageWithAgentPayload = useCallback(
        async (displayText: string, agentPayload: string) => {
            const trimmedDisplay = displayText.trim();
            if (
                trimmedDisplay === '' ||
                agentPayload.trim() === '' ||
                sendGuardRef.current
            ) {
                return;
            }
            sendGuardRef.current = true;

            const userMessage: ChatMessage = {
                id: generateMessageId(),
                role: 'user',
                content: [{ type: 'text', text: trimmedDisplay }],
                createdAt: new Date(),
            };

            beginInFlightTurn(userMessage);
            setMessages((prev) => [...prev, userMessage]);
            onMessageSent?.(userMessage);
            setShowTypingIndicator(true);

            await streamResponse(agentPayload);
        },
        [beginInFlightTurn, streamResponse, onMessageSent],
    );

    const classifyCustomSavingsGoal = useCallback(
        async (
            inputText: string,
        ): Promise<{ isGoal: boolean; label: string | null }> => {
            // Fail open (treat as a real goal) on any failure so a legitimate
            // custom goal is never blocked by a network/endpoint problem.
            const failOpen = { isGoal: true, label: null };
            const trimmed = inputText.trim();
            if (trimmed === '') {
                return failOpen;
            }
            try {
                const response = await fetchWithCurrentSessionJwt(
                    `${LLM_SERVICE_BASE_URL}/member/cmm/v2/classify-savings-goal`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(Platform.OS !== 'web'
                                ? { Origin: getWebUrl() }
                                : {}),
                        },
                        body: JSON.stringify({
                            inputText: trimmed,
                            threadId: currentThreadId.current,
                        }),
                    },
                );
                if (!response.ok) {
                    return failOpen;
                }
                const data = (await response.json()) as {
                    isGoal?: boolean;
                    label?: string | null;
                };
                return {
                    isGoal: data.isGoal ?? true,
                    label: data.label ?? null,
                };
            } catch {
                return failOpen;
            }
        },
        [],
    );

    const setBranch = useCallback(
        async (
            displayText: string,
            agentPayload: string,
            branch: string,
            options?: { allowEmptyDisplay?: boolean },
        ) => {
            const trimmedDisplay = displayText.trim();
            // `allowEmptyDisplay` persists a context-only answer (e.g. a
            // `money-goal` deep-link) with no visible bubble: the optimistic
            // user message has empty text so it renders as nothing, while the
            // full payload is still saved to the thread + branch is committed.
            if (agentPayload.trim() === '') {
                return;
            }
            if (trimmedDisplay === '' && options?.allowEmptyDisplay !== true) {
                return;
            }

            const userMessage: ChatMessage = {
                id: generateMessageId(),
                role: 'user',
                content: [{ type: 'text', text: trimmedDisplay }],
                createdAt: new Date(),
            };

            beginInFlightTurn(userMessage);
            setMessages((prev) => [...prev, userMessage]);
            onMessageSent?.(userMessage);
            persistThreadId();
            upsertVisitor();

            try {
                const response = await fetchWithCurrentSessionJwt(
                    `${LLM_SERVICE_BASE_URL}/member/cmm/set-branch`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(Platform.OS !== 'web'
                                ? { Origin: getWebUrl() }
                                : {}),
                        },
                        body: JSON.stringify({
                            branch,
                            userMessage: agentPayload,
                            threadId: currentThreadId.current,
                            resourceId,
                        }),
                    },
                );

                if (!response.ok) {
                    throw new Error(
                        `set-branch failed: ${String(response.status)}`,
                    );
                }

                setThreadMetadata((prev) => ({
                    ...(prev ?? {
                        currentStep: null,
                        collectedModules: [],
                    }),
                    branch: branch as CMMBranch,
                }));
                clearInFlightTurn();
            } catch {
                // Endpoint unreachable or failed — fall back to the LLM
                // stream path so the message is still persisted to the thread.
                await streamResponse(agentPayload);
            }
        },
        [
            beginInFlightTurn,
            clearInFlightTurn,
            onMessageSent,
            persistThreadId,
            upsertVisitor,
            resourceId,
            streamResponse,
        ],
    );

    const sendSilentMessage = useCallback(
        async (content: string, options?: SendSilentMessageOptions) => {
            if (content.trim() === '') {
                return;
            }

            setShowTypingIndicator(options?.showTypingIndicator ?? false);
            await streamResponse(content);
        },
        [streamResponse],
    );

    const requestGreeting = useCallback(async () => {
        await streamResponse('start');
    }, [streamResponse]);

    useEffect(() => {
        if (!autoGreet) {
            return;
        }

        if (
            hasGreetedRef.current ||
            isRestoringHistory ||
            hasExistingConversationRef.current ||
            messages.length > 0
        ) {
            return;
        }

        hasGreetedRef.current = true;
        void requestGreeting();
    }, [autoGreet, isRestoringHistory, messages.length, requestGreeting]);

    return {
        messages,
        threadMetadata,
        isStreaming,
        showTypingIndicator,
        error,
        isCutOff: offTopicCountRef.current >= OFF_TOPIC_CUTOFF,
        isRestoringHistory,
        threadId,
        sendMessage,
        sendMessageWithAgentPayload,
        classifyCustomSavingsGoal,
        setBranch,
        sendSilentMessage,
        requestGreeting,
        clearMessages,
        switchToThread,
        stopStreaming,
        addCollectedModule: (module: OnboardingChatModule) => {
            const existing =
                pendingCollectedModulesRef.current ??
                threadMetadata?.collectedModules ??
                [];
            if (!existing.includes(module)) {
                pendingCollectedModulesRef.current = [...existing, module];
            }
        },
    };
};
